export type LinkPreview = {
  url: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
};

const URL_PATTERN = /\bhttps?:\/\/[^\s<>"'()]+/gi;
const YOUTUBE_ID_PATTERN = /^[\w-]{11}$/;

function stripTrailingPunctuation(value: string) {
  return value.replace(/[.,;:!?]+$/g, "").replace(/\)+$/g, "");
}

export function extractFirstUrl(text: string): string | null {
  const match = text.match(URL_PATTERN)?.[0];
  if (!match) {
    return null;
  }

  try {
    const url = new URL(stripTrailingPunctuation(match));
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

function isYouTubeHost(hostname: string) {
  const host = hostname.replace(/^www\./, "").toLowerCase();
  return (
    host === "youtu.be" ||
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "music.youtube.com" ||
    host === "youtube-nocookie.com"
  );
}

export function youtubeVideoId(urlString: string): string | null {
  try {
    const url = new URL(urlString);
    if (!isYouTubeHost(url.hostname)) {
      return null;
    }

    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0] ?? "";
      return YOUTUBE_ID_PATTERN.test(id) ? id : null;
    }

    const fromQuery = url.searchParams.get("v");
    if (fromQuery && YOUTUBE_ID_PATTERN.test(fromQuery)) {
      return fromQuery;
    }

    const parts = url.pathname.split("/").filter(Boolean);
    if (
      parts.length >= 2 &&
      ["embed", "shorts", "live", "v"].includes(parts[0] ?? "") &&
      YOUTUBE_ID_PATTERN.test(parts[1] ?? "")
    ) {
      return parts[1] ?? null;
    }
  } catch {
    return null;
  }

  return null;
}

export function youtubeEmbedUrl(videoId: string, autoplay = false) {
  const params = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    controls: "1",
    rel: "0",
    fs: "1",
    iv_load_policy: "3",
    disablekb: "0",
  });
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

export function youtubeThumbnailUrl(videoId: string) {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function youtubePreviewFromUrl(url: string): LinkPreview | null {
  const videoId = youtubeVideoId(url);
  if (!videoId) {
    return null;
  }

  return {
    url,
    title: "YouTube videó",
    description: null,
    imageUrl: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    siteName: "YouTube",
  };
}

export function linkPreviewHost(preview: LinkPreview) {
  try {
    return new URL(preview.url).hostname.replace(/^www\./, "");
  } catch {
    return preview.siteName ?? preview.url;
  }
}

export function parseLinkPreview(value: unknown): LinkPreview | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const preview = value as Record<string, unknown>;
  if (typeof preview.url !== "string" || typeof preview.title !== "string") {
    return null;
  }

  try {
    const url = new URL(preview.url);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
  } catch {
    return null;
  }

  const title = preview.title.trim().slice(0, 200);
  if (!title) {
    return null;
  }

  return {
    url: preview.url,
    title,
    description:
      typeof preview.description === "string" && preview.description.trim()
        ? preview.description.trim().slice(0, 300)
        : null,
    imageUrl:
      typeof preview.imageUrl === "string" && preview.imageUrl.startsWith("http")
        ? preview.imageUrl.slice(0, 2000)
        : null,
    siteName:
      typeof preview.siteName === "string" && preview.siteName.trim()
        ? preview.siteName.trim().slice(0, 120)
        : null,
  };
}
